import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';

type ToastType = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue>({
  showToast: () => {},
});

const typeConfig: Record<ToastType, { icon: string; color: string }> = {
  success: { icon: 'ri-checkbox-circle-fill', color: 'text-green-500' },
  error: { icon: 'ri-error-warning-fill', color: 'text-red-500' },
  info: { icon: 'ri-information-fill', color: 'text-gold-600' },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idRef = useRef(0);

  const showToast = useCallback((message: string, type: ToastType = 'success') => {
    const id = ++idRef.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 2400);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed top-16 left-0 right-0 z-[100] flex flex-col items-center gap-2 px-6 pointer-events-none">
        {toasts.map((toast) => {
          const config = typeConfig[toast.type];
          return (
            <div
              key={toast.id}
              className="toast-in flex items-center gap-2 bg-brown-950/95 backdrop-blur-sm text-white rounded-full px-4 py-2.5 shadow-lg max-w-full"
            >
              <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className={`${config.icon} ${config.color} text-base`}></i>
              </span>
              <span className="text-sm whitespace-nowrap overflow-hidden text-ellipsis">
                {toast.message}
              </span>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}